import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiArrowRight, FiSearch, FiBook, FiUsers, FiSettings, FiCreditCard, 
  FiShield, FiZap, FiMessageCircle, FiMail, FiChevronDown, FiChevronUp, FiHelpCircle,
  FiLayout, FiCheckSquare, FiFolder
} from 'react-icons/fi';
import PublicPageHeader from '../../components/common/PublicPageHeader';
import PublicPageFooter from '../../components/common/PublicPageFooter';
import HelpArticleModal, { helpArticles } from '../../components/common/HelpArticleModal';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const openArticle = (articleTitle) => {
    const article = helpArticles[articleTitle];
    if (article) {
      setSelectedArticle({ title: articleTitle, ...article });
    }
  };

  const closeArticle = () => {
    setSelectedArticle(null);
  };

  const categories = [
    {
      icon: FiCheckSquare,
      title: 'Getting Started',
      description: 'Learn the basics of using Tasklina',
      color: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      articles: ['Creating your first project', 'Adding and managing tasks', 'Inviting team members', 'Setting up notifications']
    },
    {
      icon: FiFolder,
      title: 'Projects & Tasks',
      description: 'Master project and task management',
      color: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      articles: ['Project organization', 'Task priorities and labels', 'Due dates and reminders', 'Subtasks and checklists']
    },
    {
      icon: FiLayout,
      title: 'Views & Boards',
      description: 'Customize how you see your work',
      color: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      articles: ['Kanban board basics', 'List and table views', 'Filtering and sorting', 'Saved views']
    },
    {
      icon: FiUsers,
      title: 'Team Collaboration',
      description: 'Work better together',
      color: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      articles: ['Team workspaces', 'Comments and mentions', 'Activity feeds', 'Real-time collaboration']
    },
    {
      icon: FiSettings,
      title: 'Settings & Preferences',
      description: 'Customize your experience',
      color: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400',
      articles: ['Account settings', 'Notification preferences', 'Theme customization', 'Keyboard shortcuts']
    },
    {
      icon: FiShield,
      title: 'Security & Privacy',
      description: 'Keep your data safe',
      color: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      articles: ['Two-factor authentication', 'Password security', 'Data export', 'Privacy controls']
    }
  ];

  const faqs = [
    {
      question: 'How do I create a new project?',
      answer: 'Click the "+" button in the sidebar or go to Projects and click "New Project". Enter a name, description, and choose your preferred view (Kanban, List, etc.). You can always change these settings later.'
    },
    {
      question: 'Can I invite team members to specific projects?',
      answer: 'Yes! Open the project settings and go to the "Members" tab. You can invite team members by email and assign them roles like Admin, Editor, or Viewer to control their access level.'
    },
    {
      question: 'How do I set up email notifications?',
      answer: 'Go to Settings > Notifications to customize your email preferences. You can choose to receive notifications for task assignments, comments, due dates, and more. You can also set quiet hours.'
    },
    {
      question: 'Is there a mobile app available?',
      answer: 'Tasklina is fully responsive and works great on mobile browsers. We are actively working on native iOS and Android apps which will be available soon.'
    },
    {
      question: 'How do I export my data?',
      answer: 'Go to Settings > Data Export to download your projects, tasks, and comments in JSON or CSV format. Your data belongs to you, and you can export it anytime.'
    },
    {
      question: 'What happens when I delete a task?',
      answer: 'Deleted tasks are moved to the Trash where they remain for 30 days. You can restore them anytime during this period. After 30 days, they are permanently deleted.'
    },
    {
      question: 'How do I upgrade to the Professional plan?',
      answer: 'Go to Settings > Billing and click "Upgrade to Professional". You can start with a 14-day free trial without entering payment details.'
    },
    {
      question: 'Can I use Tasklina offline?',
      answer: 'Currently, Tasklina requires an internet connection. However, we are working on offline support that will allow you to view and edit tasks without connectivity, with automatic sync when you are back online.'
    }
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const filteredFaqs = searchQuery 
    ? faqs.filter(faq => 
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqs;

  // Filter articles based on search query
  const filteredArticles = searchQuery
    ? Object.keys(helpArticles).filter(title => 
        title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        helpArticles[title].content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />

      {/* Hero Section with Search */}
      <section className="bg-gradient-to-b from-blue-50 to-white dark:from-gray-800 dark:to-gray-900 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex p-3 bg-blue-100 dark:bg-blue-900/50 rounded-2xl mb-6">
              <FiHelpCircle className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              How Can We Help?
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
              Find answers, learn how to use Tasklina, and get support from our team.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search for help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Search Results Section - shows when searching */}
      {searchQuery && filteredArticles.length > 0 && (
        <section className="py-8 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Articles matching "{searchQuery}" ({filteredArticles.length})
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredArticles.map((title, idx) => (
                <button
                  key={idx}
                  onClick={() => openArticle(title)}
                  className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-left hover:shadow-md transition-shadow"
                >
                  <FiBook className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div>
                    <span className="text-gray-900 dark:text-white font-medium text-sm">{title}</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{helpArticles[title].category}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories Grid */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Browse by Topic</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className={`w-12 h-12 rounded-xl ${category.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{category.title}</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">{category.description}</p>
                  <ul className="space-y-2">
                    {category.articles.map((article, idx) => (
                      <li key={idx}>
                        <button 
                          onClick={() => openArticle(article)}
                          className="text-blue-600 text-sm hover:text-blue-700 transition-colors flex items-center space-x-1 hover:underline"
                        >
                          <FiBook className="w-3.5 h-3.5" />
                          <span>{article}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Quick answers to common questions
            </p>
          </div>

          <div className="space-y-3">
            {filteredFaqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <span className="text-lg font-medium text-gray-900 dark:text-white pr-4">{faq.question}</span>
                  {expandedFaq === index ? (
                    <FiChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <FiChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredFaqs.length === 0 && (
            <div className="text-center py-12">
              <FiSearch className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No matching questions found. Try a different search term.</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h2 className="text-3xl font-bold mb-4">Still Need Help?</h2>
                <p className="text-blue-100 text-lg mb-6">
                  Our support team is here to assist you. Reach out and we'll get back to you as soon as possible.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-white text-blue-600 rounded-full font-semibold hover:bg-blue-50 transition-colors"
                  >
                    <FiMessageCircle className="w-5 h-5" />
                    <span>Contact Support</span>
                  </Link>
                  <a
                    href="mailto:support@tasklina.com"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-400 transition-colors"
                  >
                    <FiMail className="w-5 h-5" />
                    <span>Email Us</span>
                  </a>
                </div>
              </div>
              <div className="hidden md:flex justify-center">
                <div className="bg-blue-500/30 rounded-2xl p-8">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 text-white">
                      <FiZap className="w-5 h-5" />
                      <span>Fast Response Time</span>
                    </div>
                    <div className="flex items-center space-x-3 text-white">
                      <FiUsers className="w-5 h-5" />
                      <span>Dedicated Support Team</span>
                    </div>
                    <div className="flex items-center space-x-3 text-white">
                      <FiBook className="w-5 h-5" />
                      <span>Comprehensive Documentation</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 border-t border-gray-100 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <Link to="/pricing" className="group">
              <div className="p-6 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <FiCreditCard className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Pricing & Plans</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Compare features and choose your plan</p>
                <span className="inline-flex items-center space-x-1 text-blue-600 text-sm mt-2 group-hover:underline">
                  <span>View Pricing</span>
                  <FiArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
            <Link to="/integrations" className="group">
              <div className="p-6 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <FiZap className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Integrations</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Connect with your favorite tools</p>
                <span className="inline-flex items-center space-x-1 text-blue-600 text-sm mt-2 group-hover:underline">
                  <span>Explore</span>
                  <FiArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
            <Link to="/about" className="group">
              <div className="p-6 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <FiUsers className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">About Us</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Learn about our mission and team</p>
                <span className="inline-flex items-center space-x-1 text-blue-600 text-sm mt-2 group-hover:underline">
                  <span>Learn More</span>
                  <FiArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <PublicPageFooter />

      {/* Help Article Modal */}
      {selectedArticle && (
        <HelpArticleModal
          article={selectedArticle}
          articleTitle={selectedArticle.title}
          onClose={closeArticle}
        />
      )}
    </div>
  );
};

export default HelpCenter;
